import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
} from "@mui/material";
import React from "react";
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import CloseIcon from "@mui/icons-material/Close";

interface ModalRatingProps {
  modalOpen: boolean;
  /** Cierre “clásico” (fallback). Mantengo compatibilidad hacia atrás. */
  handleCloseModal?: () => void;
  /** Nuevo: callback con razón de cierre (backdrop/escape/icono). */
  onRequestClose?: (ev?: unknown, reason?: "backdropClick" | "escapeKeyDown" | "closeIcon") => void;

  handleGuardarCalificacion: () => void;
  rating: number;
  handleRating: (value: number) => void;

  /** Si true, obliga a calificar antes de cerrar. Default: true */
  requireRating?: boolean;
  /** Estado de guardado para deshabilitar controles. Default: false */
  saving?: boolean;
}

export const ModalRating = ({
  modalOpen,
  handleCloseModal,         // opcional (compatibilidad existente)
  onRequestClose,            // nuevo
  rating,
  handleRating,
  handleGuardarCalificacion,
  requireRating = true,
  saving = false,
}: ModalRatingProps) => {

  const canClose = !requireRating || rating > 0;

  // Decide a qué callback llamar al cerrar
  const closeVia = (reason?: "backdropClick" | "escapeKeyDown" | "closeIcon") => {
    if (onRequestClose) return onRequestClose(undefined, reason);
    if (handleCloseModal) return handleCloseModal();
  };

  // Bloquea cierre por backdrop/ESC cuando no ha calificado
  const handleDialogClose = (_event: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
    if (!canClose || saving) return;
    closeVia(reason);
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleDialogClose}
      disableEscapeKeyDown={!canClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(29, 61, 77, 0.6)" } }}
      PaperProps={{ sx: { borderRadius: 5 } }}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          if (!canClose || saving) return;
          closeVia("closeIcon");
        }}
        disabled={!canClose || saving}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "#000000",
          opacity: (!canClose || saving) ? 0.5 : 1,
          "&:disabled": { opacity: 0.35 },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle
        sx={{
          color: "#336B87",
          fontFamily: "Montserrat",
          fontWeight: "bold",
          fontSize: 20,
          textAlign: "center",
          mt: 3,
        }}
      >
        Calificación de resultado
      </DialogTitle>

      <DialogContent
        sx={{
          color: "rgba(0, 0, 0, 0.68)",
          fontFamily: "Montserrat",
          fontSize: 14,
          textAlign: "center",
        }}
      >
        Por favor te pedimos que califiques la actividad realizada en una escala
        de 1 a 5 estrellas.
        <br />
        <br />
        <Rating
          name="calificación"
          value={rating}
          onChange={(_event, newValue) => {
            if (newValue != null) {
              handleRating(newValue);
            }
          }}
          icon={<StarRateRoundedIcon sx={{ fontSize: 30 }} />}
          emptyIcon={<StarBorderRoundedIcon sx={{ fontSize: 30 }} />}
          sx={{
            "& .MuiRating-iconFilled": { color: "#FBCB65" },
            "& .MuiRating-iconHover": { color: "#f9ba32" },
            "& .MuiRating-iconEmpty": { color: "rgba(0,0,0,0.2)" },
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          sx={{
            display: "flex",
            m: "auto",
            width: "100%",
            mb: 3,
            ml: 2,
            mr: 2,
            height: 73,
            backgroundColor: " #336B87",
            borderRadius: 5,
            fontSize: 14,
            fontWeight: "SemiBold",
            fontFamily: "Montserrat",
            "&:hover": { backgroundColor: " #285368ff" },
          }}
          onClick={handleGuardarCalificacion}
          disabled={saving || rating <= 0}   // no permite guardar sin calificar
        >
          {saving ? "Guardando…" : "Calificar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
